import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Contact } from '../types';
import { showToast } from '../utils/toast';

type DbContact = Database['public']['Tables']['contacts']['Row'];
type DbContactInsert = Database['public']['Tables']['contacts']['Insert'];
type DbContactUpdate = Database['public']['Tables']['contacts']['Update'];

type DbContactWithRelations = DbContact & { event_contacts?: { event_id: string }[] };

const mapToContact = (dbContact: DbContactWithRelations): Contact => ({
  id: dbContact.id,
  name: `${dbContact.first_name} ${dbContact.last_name || ''}`.trim(),
  role: dbContact.role || undefined,
  email: dbContact.email || undefined,
  phone: dbContact.phone || undefined,
  notes: dbContact.notes || undefined,
  tags: dbContact.tags || [],
  eventIds: dbContact.event_contacts?.map((ec) => ec.event_id) || [],
  linkedEntityIds: [], // handled natively via events where contact_id = this.id
  contactType: dbContact.contact_type || undefined,
  website: dbContact.website || undefined,
  linkedinUrl: dbContact.linkedin_url || undefined,
  siret: dbContact.siret || undefined,
  tvaNumber: dbContact.tva_number || undefined,
  sector: dbContact.sector || undefined,
  professionalAddress: dbContact.professional_address || undefined,
  customFields: (dbContact.custom_fields as Array<{ label: string; value: string }>) || undefined,
});

export const contactService = {
  async getContacts(): Promise<Contact[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      const { data, error } = await supabase
        .from('contacts')
        .select('*, event_contacts(event_id)')
        .order('first_name', { ascending: true });

      if (error) {
        console.error('[contact.service] getContacts:', error.message);
        showToast('Erreur lors du chargement des contacts', 'error');
        return [];
      }
      return (data || []).map(mapToContact);
    } catch (err: any) {
      console.error('[contact.service] getContacts:', err.message);
      showToast('Erreur lors du chargement des contacts', 'error');
      return [];
    }
  },

  async createContact(contact: Partial<Contact>): Promise<Contact> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const nameParts = (contact.name || 'Nouveau Contact').split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    const insertData: DbContactInsert = {
      user_id: user.id,
      first_name: firstName,
      last_name: lastName || null,
      role: contact.role || null,
      email: contact.email || null,
      phone: contact.phone || null,
      notes: contact.notes || null,
      tags: contact.tags || [],
      contact_type: contact.contactType || null,
      website: contact.website || null,
      linkedin_url: contact.linkedinUrl || null,
      siret: contact.siret || null,
      tva_number: contact.tvaNumber || null,
      sector: contact.sector || null,
      professional_address: contact.professionalAddress || null,
      custom_fields: contact.customFields
        ? (contact.customFields as unknown as Database['public']['Tables']['contacts']['Insert']['custom_fields'])
        : null,
    };

    const { data, error } = await supabase
      .from('contacts')
      .insert(insertData)
      .select('*, event_contacts(event_id)')
      .single();

    if (error) {
      console.error('[contact.service] createContact:', error.message);
      showToast('Impossible de créer le contact', 'error');
      throw error;
    }

    if (contact.eventIds && contact.eventIds.length > 0) {
      const relations = contact.eventIds.map((eventId) => ({
        contact_id: data.id,
        event_id: eventId,
        user_id: user.id,
      }));
      const { error: relError } = await supabase.from('event_contacts').insert(relations);
      if (relError) console.error('Could not insert event_contacts', relError);
      data.event_contacts = relations;
    }

    return mapToContact(data);
  },

  async updateContact(id: string, updates: Partial<Contact>): Promise<Contact> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const updateData: DbContactUpdate = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) {
      const nameParts = updates.name.split(' ');
      updateData.first_name = nameParts[0];
      updateData.last_name = nameParts.slice(1).join(' ') || null;
    }

    if (updates.role !== undefined) updateData.role = updates.role;
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.phone !== undefined) updateData.phone = updates.phone;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.contactType !== undefined) updateData.contact_type = updates.contactType;
    if (updates.website !== undefined) updateData.website = updates.website;
    if (updates.linkedinUrl !== undefined) updateData.linkedin_url = updates.linkedinUrl;
    if (updates.siret !== undefined) updateData.siret = updates.siret;
    if (updates.tvaNumber !== undefined) updateData.tva_number = updates.tvaNumber;
    if (updates.sector !== undefined) updateData.sector = updates.sector;
    if (updates.professionalAddress !== undefined)
      updateData.professional_address = updates.professionalAddress;
    if (updates.customFields !== undefined)
      updateData.custom_fields =
        updates.customFields as unknown as Database['public']['Tables']['contacts']['Update']['custom_fields'];

    const { data, error } = await supabase
      .from('contacts')
      .update(updateData)
      .eq('id', id)
      .select('*, event_contacts(event_id)')
      .single();

    if (error) {
      console.error('[contact.service] updateContact:', error.message);
      showToast('Impossible de mettre à jour le contact', 'error');
      throw error;
    }

    if (updates.eventIds !== undefined) {
      const currentEventIds = data.event_contacts?.map((ec) => ec.event_id) || [];
      const newEventIds = updates.eventIds || [];

      const toAdd = newEventIds.filter((eventId) => !currentEventIds.includes(eventId));
      const toRemove = currentEventIds.filter((eventId) => !newEventIds.includes(eventId));

      if (toAdd.length > 0) {
        await supabase
          .from('event_contacts')
          .insert(
            toAdd.map((eventId) => ({ contact_id: id, event_id: eventId, user_id: user.id }))
          );
      }
      if (toRemove.length > 0) {
        await supabase
          .from('event_contacts')
          .delete()
          .eq('contact_id', id)
          .in('event_id', toRemove);
      }

      data.event_contacts = newEventIds.map((eventId) => ({ event_id: eventId }));
    }

    return mapToContact(data);
  },

  async deleteContact(id: string): Promise<void> {
    const { error } = await supabase.from('contacts').delete().eq('id', id);

    if (error) {
      console.error('[contact.service] deleteContact:', error.message);
      showToast('Impossible de supprimer le contact', 'error');
      throw error;
    }
  },
};
